/**
 * ProHair Search Enhancement v6
 * Phase 1: Auto-focus, keyboard shortcuts, scroll lock
 * Phase 2: Add-to-Cart buttons in search results (Vitals + GF)
 * Phase 3: Typo correction + zero-results fallback (targets Vitals search)
 * Phase 4: Search analytics (GA4 via dataLayer)
 * Phase 5: Search Unification v6 — suppresses Vitals/GF, auto-focuses native predictive search
 */
(function() {
  'use strict';

  /* ===== PHASE 1: Search UX Improvements ===== */

  // Auto-focus search input when modal opens
  function initAutoFocus() {
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          var target = m.target;
          if (target.classList && target.classList.contains('search--opened')) {
            setTimeout(function() {
              var input = document.querySelector('.search__input-field') ||
                          document.querySelector('input[name="q"][type="search"]') ||
                          document.querySelector('.header__search input');
              if (input) {
                input.focus();
                input.select();
              }
            }, 150);
          }
        }
      });
    });
    var body = document.body;
    if (body) {
      observer.observe(body, { attributes: true, subtree: false });
    }
    // Also observe the header for class changes
    var header = document.querySelector('.header') || document.querySelector('header');
    if (header) {
      observer.observe(header, { attributes: true, subtree: true });
    }
  }

  // Keyboard shortcut: "/" to open search
  function initKeyboardShortcut() {
    document.addEventListener('keydown', function(e) {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        var tag = (e.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;
        e.preventDefault();
        var searchBtn = document.querySelector('.header__actions_btn--search') ||
                        document.querySelector('[data-action="toggle-search"]') ||
                        document.querySelector('button[aria-label="Search"]');
        if (searchBtn) searchBtn.click();
      }
      // Escape to close search
      if (e.key === 'Escape') {
        var closeBtn = document.querySelector('.vitals-search-close') ||
                       document.querySelector('.search__close') ||
                       document.querySelector('[data-action="close-search"]');
        if (closeBtn) closeBtn.click();
      }
    });
  }

  // Helper: check if element is visible (works for position:fixed too)
  function isElementVisible(el) {
    if (!el) return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  // The native drawer owns the search scroll lock. Vitals is suppressed, but
  // its trigger can still add a body class; release only the lock this search opened.
  function initScrollLock() {
    var drawer = document.getElementById('predictive__search_overlay');
    if (!drawer) return;
    var wasOpen = false;
    var previousOverflow = '';
    var hadVitalsLock = document.body.classList.contains('vtl-g-not-scrollable');
    document.addEventListener('click', function(e) {
      if (!wasOpen && e.target.closest('.header__actions_btn--search')) {
        hadVitalsLock = document.body.classList.contains('vtl-g-not-scrollable');
      }
    }, true);
    function syncSearchLock() {
      var isOpen = drawer.classList.contains('active');
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;
      if (isOpen) {
        previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = previousOverflow;
        if (!hadVitalsLock) document.body.classList.remove('vtl-g-not-scrollable');
      }
    }
    new MutationObserver(syncSearchLock).observe(drawer, {
      attributes: true, attributeFilter: ['class']
    });
    syncSearchLock();
  }

  /* ===== PHASE 2: Add-to-Cart in Search Results ===== */

  // Shared: AJAX add to cart
  function handleAddToCart(btn, variantId) {
    if (btn.classList.contains('prohair-search-atc--adding') ||
        btn.classList.contains('prohair-search-atc--soldout')) return;

    btn.classList.add('prohair-search-atc--adding');
    var originalHTML = btn.innerHTML;
    btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      btn.classList.remove('prohair-search-atc--adding');
      if (data.items || data.id) {
        btn.classList.add('prohair-search-atc--added');
        btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg><span class="prohair-search-atc__text">Added!</span>';
        refreshCartCount();
        setTimeout(function() {
          btn.classList.remove('prohair-search-atc--added');
          btn.innerHTML = originalHTML;
        }, 2000);
      } else {
        btn.innerHTML = originalHTML;
      }
    })
    .catch(function() {
      btn.classList.remove('prohair-search-atc--adding');
      btn.innerHTML = originalHTML;
    });
  }

  // Shared: refresh cart count badge
  function refreshCartCount() {
    fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var badges = document.querySelectorAll('.header__cart-count, .cart-count, .cart-count-bubble, [data-cart-count], .js-cart-count');
        badges.forEach(function(b) {
          b.textContent = cart.item_count;
          if (cart.item_count > 0) b.style.display = '';
        });
      })
      .catch(function() {});
  }

  // Create ATC button element
  function createATCButton(variantId, available) {
    var btn = document.createElement('button');
    btn.className = 'prohair-search-atc' + (!available ? ' prohair-search-atc--soldout' : '');
    btn.setAttribute('type', 'button');
    if (variantId) btn.setAttribute('data-variant-id', variantId);

    if (available) {
      btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg><span class="prohair-search-atc__text">Add</span>';
    } else {
      btn.innerHTML = '<span class="prohair-search-atc__text">Sold Out</span>';
    }
    return btn;
  }

  /* --- Vitals Search ATC Injection --- */

  var vitalsProductCache = {};
  var vitalsInjectQueue = {};

  function injectVitalsATCButtons() {
    var items = document.querySelectorAll('.vitals-search-item');
    if (!items.length) return;

    items.forEach(function(item) {
      if (item.getAttribute('data-atc-done')) return;
      item.setAttribute('data-atc-done', '1');

      var link = item.querySelector('a[href*="/products/"]');
      if (!link) return;

      var href = link.getAttribute('href');
      var handle = href.split('/products/')[1];
      if (!handle) return;
      handle = handle.split('?')[0].split('#')[0];

      // Find the price container to insert button after it
      var pricesDiv = item.querySelector('.vitals-search-prices');

      // Create a wrapper for the button that sits outside the <a> tag
      var btnWrap = document.createElement('div');
      btnWrap.className = 'prohair-vitals-atc-wrap';

      // Insert button wrapper after the link inside the <li>
      item.appendChild(btnWrap);

      // Check cache first
      if (vitalsProductCache[handle]) {
        var cached = vitalsProductCache[handle];
        var btn = createATCButton(cached.variantId, cached.available);
        btnWrap.appendChild(btn);
        return;
      }

      // Fetch product data
      fetch('/products/' + handle + '.js')
        .then(function(r) { return r.json(); })
        .then(function(product) {
          var variant = null;
          var available = product.available;
          if (available && product.variants) {
            variant = product.variants.find(function(v) { return v.available; });
          }
          var variantId = variant ? variant.id : (product.variants && product.variants[0] ? product.variants[0].id : null);

          // Cache it
          vitalsProductCache[handle] = { variantId: variantId, available: available };

          var btn = createATCButton(variantId, available);
          btnWrap.appendChild(btn);
        })
        .catch(function() {
          // Silently fail - no button shown
        });
    });
  }

  function initVitalsSearchATC() {
    // Debounced injection — fires on any DOM mutation, checks for unprocessed items
    var vitalsDebounce = null;
    var observer = new MutationObserver(function() {
      if (vitalsDebounce) clearTimeout(vitalsDebounce);
      vitalsDebounce = setTimeout(function() {
        // Only run if there are unprocessed vitals items
        var unprocessed = document.querySelectorAll('.vitals-search-item:not([data-atc-done])');
        if (unprocessed.length) injectVitalsATCButtons();
      }, 200);
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Also inject on any existing results (page might already have them)
    setTimeout(injectVitalsATCButtons, 500);
    setTimeout(injectVitalsATCButtons, 1500);

    // Click handler for Vitals ATC buttons (capture phase to beat other handlers)
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.prohair-search-atc');
      if (!btn) return;

      // Only handle if inside vitals search
      if (!btn.closest('.vitals-search-item') && !btn.closest('.prohair-vitals-atc-wrap')) return;

      e.preventDefault();
      // stopPropagation removed — blocks Phase 4 analytics handlers

      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId || btn.classList.contains('prohair-search-atc--soldout')) return;
      handleAddToCart(btn, variantId);
    }, true);
  }

  /* --- GF (Globo Filter) Search ATC Injection --- */

  function injectGFButtons() {
    var items = document.querySelectorAll('.gf-search-products .ui-menu-item');
    if (!items.length) return;

    items.forEach(function(item) {
      if (item.getAttribute('data-atc-done')) return;
      item.setAttribute('data-atc-done', '1');

      var rightDiv = item.querySelector('.gf-search-right');
      var link = item.querySelector('a');
      if (!rightDiv || !link) return;

      var href = link.getAttribute('href');
      if (!href || href.indexOf('/products/') === -1) return;
      var handle = href.split('/products/')[1].split('?')[0].split('#')[0];

      fetch('/products/' + handle + '.js')
        .then(function(r) { return r.json(); })
        .then(function(product) {
          var variant = product.available && product.variants ?
            product.variants.find(function(v) { return v.available; }) : null;
          var btn = createATCButton(
            variant ? variant.id : (product.variants[0] ? product.variants[0].id : null),
            product.available
          );
          rightDiv.appendChild(btn);
        })
        .catch(function() {});
    });
  }

  function initGFSearchATC() {
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        if (m.addedNodes.length) {
          setTimeout(injectGFButtons, 200);
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Click handler for GF ATC buttons
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.prohair-search-atc');
      if (!btn) return;
      if (!btn.closest('.gf-search-products')) return;

      e.preventDefault();
      // stopPropagation removed — blocks Phase 4 analytics handlers

      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId || btn.classList.contains('prohair-search-atc--soldout')) return;
      handleAddToCart(btn, variantId);
    }, true);
  }

  /* ===== PHASE 3: Typo Correction + Zero-Results Fallback (Vitals Search) ===== */

  // Dictionary of known terms customers search for
  var SEARCH_DICTIONARY = {
    brands: [
      'Redken', 'Pureology', 'Moroccanoil', 'Olaplex', 'Matrix',
      'Biolage', 'Kerastase', 'Joico', 'Kenra', 'Paul Mitchell',
      'Nioxin', 'Wella', 'Schwarzkopf', 'Verb', 'Amika',
      'Living Proof', 'Bumble and Bumble', 'Aveda', 'CHI',
      'Sebastian', 'TIGI', 'AG Hair', 'DevaCurl', 'KMS',
      'Kerachroma'
    ],
    concerns: [
      'Dry Hair', 'Color Treated', 'Bond Repair', 'Damaged Hair',
      'Frizzy Hair', 'Thinning Hair', 'Oily Hair', 'Curly Hair',
      'Fine Hair', 'Thick Hair', 'Blonde Hair', 'Volume',
      'Hair Loss', 'Dandruff', 'Split Ends', 'Heat Protection',
      'Scalp Care', 'Moisture', 'Strengthening', 'Hydrating'
    ],
    types: [
      'Shampoo', 'Conditioner', 'Hair Mask', 'Hair Oil',
      'Leave In', 'Leave-In Conditioner', 'Styling Cream',
      'Hair Spray', 'Hairspray', 'Mousse', 'Gel', 'Serum',
      'Dry Shampoo', 'Treatment', 'Hair Color', 'Developer',
      'Toner', 'Gloss', 'Blow Dry', 'Texture Spray',
      'Edge Control', 'Pomade', 'Wax', 'Clay', 'Paste'
    ]
  };

  // Curated fallback suggestions when zero results
  var FALLBACK_LINKS = [
    { label: 'Redken', url: '/collections/redken' },
    { label: 'Pureology', url: '/collections/pureology' },
    { label: 'Moroccanoil', url: '/collections/moroccanoil' },
    { label: 'Olaplex', url: '/collections/olaplex' },
    { label: 'Shampoo', url: '/collections/all?q=shampoo' },
    { label: 'Conditioner', url: '/collections/all?q=conditioner' },
    { label: 'Hair Masks', url: '/collections/all?q=mask' },
    { label: 'Shop All', url: '/collections/all' }
  ];

  // Build flat list of all known terms for matching
  var ALL_TERMS = [];
  Object.keys(SEARCH_DICTIONARY).forEach(function(cat) {
    SEARCH_DICTIONARY[cat].forEach(function(term) {
      ALL_TERMS.push(term);
    });
  });

  // Levenshtein distance
  function levenshtein(a, b) {
    a = a.toLowerCase();
    b = b.toLowerCase();
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;

    var matrix = [];
    var i, j;
    for (i = 0; i <= b.length; i++) matrix[i] = [i];
    for (j = 0; j <= a.length; j++) matrix[0][j] = j;

    for (i = 1; i <= b.length; i++) {
      for (j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[b.length][a.length];
  }

  // Find best matching terms for a query
  function findSuggestions(query) {
    if (!query || query.length < 3) return [];

    var q = query.toLowerCase().trim();
    var results = [];

    ALL_TERMS.forEach(function(term) {
      var t = term.toLowerCase();
      if (q === t) return;
      if (t.indexOf(q) === 0) return;

      var dist = levenshtein(q, t);
      var maxLen = Math.max(q.length, t.length);
      var similarity = 1 - (dist / maxLen);

      var prefixMatch = q.substring(0, 2) === t.substring(0, 2);
      var strongPrefix = q.length >= 3 && t.length >= 3 && q.substring(0, 3) === t.substring(0, 3);

      var threshold = prefixMatch ? 0.45 : 0.55;
      if (strongPrefix) threshold = 0.4;

      if (similarity >= threshold && dist <= Math.ceil(maxLen * 0.5)) {
        results.push({
          term: term,
          distance: dist,
          similarity: similarity,
          prefixMatch: prefixMatch
        });
      }
    });

    results.sort(function(a, b) {
      if (a.prefixMatch !== b.prefixMatch) return a.prefixMatch ? -1 : 1;
      return a.distance - b.distance;
    });

    return results.slice(0, 3);
  }

  // Remove any existing enhancement banners
  function removeSearchEnhancements() {
    var existing = document.querySelectorAll('.prohair-typo-banner, .prohair-zero-results');
    existing.forEach(function(el) { el.remove(); });
  }

  // Trigger a new Vitals search by setting input value and firing events
  function triggerVitalsSearch(term) {
    var input = document.querySelector('.vitals-search-input');
    if (!input) return;

    var nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype, 'value'
    ).set;
    nativeInputValueSetter.call(input, term);

    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true, key: 'a' }));

    removeSearchEnhancements();
  }

  // Build the "Did you mean?" banner HTML
  function buildTypoBanner(suggestions) {
    var banner = document.createElement('div');
    banner.className = 'prohair-typo-banner';

    var label = document.createElement('span');
    label.className = 'prohair-typo-banner__label';
    label.textContent = 'Did you mean: ';
    banner.appendChild(label);

    suggestions.forEach(function(s, idx) {
      if (idx > 0) {
        var sep = document.createElement('span');
        sep.className = 'prohair-typo-banner__sep';
        sep.textContent = idx === suggestions.length - 1 ? ' or ' : ', ';
        banner.appendChild(sep);
      }

      var link = document.createElement('button');
      link.className = 'prohair-typo-banner__suggestion';
      link.setAttribute('type', 'button');
      link.setAttribute('data-suggestion', s.term);
      link.textContent = s.term;
      banner.appendChild(link);
    });

    var questionMark = document.createElement('span');
    questionMark.className = 'prohair-typo-banner__label';
    questionMark.textContent = '?';
    banner.appendChild(questionMark);

    return banner;
  }

  // Build the zero-results fallback panel with curated links
  function buildFallbackPanel() {
    var panel = document.createElement('div');
    panel.className = 'prohair-zero-results';

    var heading = document.createElement('div');
    heading.className = 'prohair-zero-results__heading';
    heading.textContent = 'Try browsing instead:';
    panel.appendChild(heading);

    var grid = document.createElement('div');
    grid.className = 'prohair-zero-results__grid';

    FALLBACK_LINKS.forEach(function(item) {
      var link = document.createElement('a');
      link.className = 'prohair-zero-results__link';
      link.setAttribute('href', item.url);
      link.textContent = item.label;
      grid.appendChild(link);
    });

    panel.appendChild(grid);
    return panel;
  }

  // Main: monitor Vitals search for no-results state, inject typo + fallback
  function initSearchEnhancements() {
    var debounce = null;
    var lastCheckedQuery = '';

    var observer = new MutationObserver(function() {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(function() {
        // Check if Vitals no-results title exists and is visible
        var noResultEl = document.querySelector('.no-results-title');
        if (!noResultEl || !isElementVisible(noResultEl)) {
          // If Vitals has product results, clean up any stale banners
          var hasResults = document.querySelector('.vitals-search-titles--has-results');
          if (hasResults) {
            removeSearchEnhancements();
          }
          return;
        }

        // Get the current query from Vitals search input
        var input = document.querySelector('.vitals-search-input');
        if (!input || !input.value) return;

        var query = input.value.trim();
        if (query.length < 3) return;

        // Don't re-process the same query
        if (query === lastCheckedQuery) return;
        lastCheckedQuery = query;

        // Find the results container to inject into
        var container = noResultEl.closest('.results-container');
        if (!container) return;

        // Remove any previous enhancements
        removeSearchEnhancements();

        // Try typo suggestions first
        var suggestions = findSuggestions(query);
        if (suggestions.length) {
          var typoBanner = buildTypoBanner(suggestions);
          container.appendChild(typoBanner);
        }

        // Always show the fallback panel on zero results
        var fallbackPanel = buildFallbackPanel();
        container.appendChild(fallbackPanel);

      }, 350);
    });

    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    // Click handler for "Did you mean?" suggestion buttons
    document.addEventListener('click', function(e) {
      var suggBtn = e.target.closest('.prohair-typo-banner__suggestion');
      if (!suggBtn) return;

      e.preventDefault();
      // stopPropagation removed — blocks Phase 4 analytics handlers

      var term = suggBtn.getAttribute('data-suggestion');
      if (term) triggerVitalsSearch(term);
    }, true);

    // Reset lastCheckedQuery when Vitals search input changes
    var setupInputListener = function() {
      var vitalsInput = document.querySelector('.vitals-search-input');
      if (vitalsInput && !vitalsInput._prohairListening) {
        vitalsInput._prohairListening = true;
        vitalsInput.addEventListener('input', function() {
          lastCheckedQuery = '';
        });
      }
    };
    setupInputListener();
    // Also retry after a delay in case the input isn't in the DOM yet
    setTimeout(setupInputListener, 2000);
  }

  /* ===== PHASE 4: Search Analytics (GA4 via dataLayer) ===== */

  var analyticsState = {
    lastTrackedQuery: '',
    searchStartTime: 0,
    sessionSearches: 0
  };

  function trackEvent(eventName, params) {
    if (!window.dataLayer) return;
    var data = { event: eventName };
    if (params) {
      Object.keys(params).forEach(function(k) { data[k] = params[k]; });
    }
    window.dataLayer.push(data);
  }

  // Track search query submission (debounced — fires once per distinct query)
  function initSearchQueryTracking() {
    var queryDebounce = null;

    var observer = new MutationObserver(function() {
      if (queryDebounce) clearTimeout(queryDebounce);
      queryDebounce = setTimeout(function() {
        var input = document.querySelector('.vitals-search-input');
        if (!input || !input.value) return;

        var query = input.value.trim();
        if (query.length < 2) return;
        if (query === analyticsState.lastTrackedQuery) return;

        // Check if results are showing (has-results class) or no-results is visible
        var hasResults = document.querySelector('.vitals-search-titles--has-results');
        var noResults = document.querySelector('.no-results-title');
        var hasNoResults = noResults && isElementVisible(noResults);

        // Only fire once results or no-results state is confirmed
        if (!hasResults && !hasNoResults) return;

        analyticsState.lastTrackedQuery = query;
        analyticsState.sessionSearches++;
        analyticsState.searchStartTime = Date.now();

        // Count visible result items
        var resultCount = 0;
        if (hasResults) {
          resultCount = document.querySelectorAll('.vitals-search-item').length;
        }

        trackEvent('prohair_search', {
          search_term: query,
          search_results_count: resultCount,
          search_has_results: !hasNoResults,
          search_session_count: analyticsState.sessionSearches
        });

        // Fire dedicated zero-results event for easy GTM trigger
        if (hasNoResults) {
          trackEvent('prohair_search_zero_results', {
            search_term: query
          });
        }
      }, 600);
    });

    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  // Track clicks on search result products
  function initResultClickTracking() {
    document.addEventListener('click', function(e) {
      var link = e.target.closest('.vitals-search-item a[href*="/products/"]');
      if (!link) return;

      var item = link.closest('.vitals-search-item');
      if (!item) return;

      var href = link.getAttribute('href') || '';
      var handle = '';
      if (href.indexOf('/products/') !== -1) {
        handle = href.split('/products/')[1];
        if (handle) handle = handle.split('?')[0].split('#')[0];
      }

      // Get product title from the item
      var titleEl = item.querySelector('.vitals-search-item-title') ||
                    item.querySelector('.vitals-search-title') ||
                    item.querySelector('a');
      var title = titleEl ? titleEl.textContent.trim() : handle;

      // Determine position in results list
      var allItems = document.querySelectorAll('.vitals-search-item');
      var position = 0;
      for (var i = 0; i < allItems.length; i++) {
        if (allItems[i] === item) { position = i + 1; break; }
      }

      var timeSinceSearch = analyticsState.searchStartTime ?
        Math.round((Date.now() - analyticsState.searchStartTime) / 1000) : 0;

      trackEvent('prohair_search_click', {
        search_term: analyticsState.lastTrackedQuery,
        click_product_handle: handle,
        click_product_title: title.substring(0, 100),
        click_position: position,
        click_time_seconds: timeSinceSearch
      });
    });
  }

  // Track ATC from search results
  function initSearchATCTracking() {
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.prohair-search-atc');
      if (!btn) return;
      if (btn.classList.contains('prohair-search-atc--soldout')) return;
      if (btn.classList.contains('prohair-search-atc--adding')) return;

      var variantId = btn.getAttribute('data-variant-id');

      // Find the parent search item to get product info
      var item = btn.closest('.vitals-search-item') || btn.closest('.ui-menu-item');
      var handle = '';
      if (item) {
        var link = item.querySelector('a[href*="/products/"]');
        if (link) {
          var href = link.getAttribute('href') || '';
          if (href.indexOf('/products/') !== -1) {
            handle = href.split('/products/')[1];
            if (handle) handle = handle.split('?')[0].split('#')[0];
          }
        }
      }

      var source = btn.closest('.vitals-search-item') ? 'vitals' :
                   btn.closest('.gf-search-products') ? 'gf' : 'unknown';

      trackEvent('prohair_search_atc', {
        search_term: analyticsState.lastTrackedQuery,
        atc_product_handle: handle,
        atc_variant_id: variantId,
        atc_source: source
      });
    });
  }

  // Track typo correction clicks ("Did you mean?" suggestions)
  function initTypoCorrectionTracking() {
    document.addEventListener('click', function(e) {
      var suggBtn = e.target.closest('.prohair-typo-banner__suggestion');
      if (!suggBtn) return;

      var suggestedTerm = suggBtn.getAttribute('data-suggestion');
      trackEvent('prohair_search_typo_correction', {
        original_term: analyticsState.lastTrackedQuery,
        corrected_term: suggestedTerm
      });
    });
  }

  // Track fallback link clicks (zero-results curated links)
  function initFallbackClickTracking() {
    document.addEventListener('click', function(e) {
      var link = e.target.closest('.prohair-zero-results__link');
      if (!link) return;

      trackEvent('prohair_search_fallback_click', {
        search_term: analyticsState.lastTrackedQuery,
        fallback_label: link.textContent.trim(),
        fallback_url: link.getAttribute('href')
      });
    });
  }

  // Master analytics init
  function initSearchAnalytics() {
    if (!window.dataLayer) return;
    initSearchQueryTracking();
    initResultClickTracking();
    initSearchATCTracking();
    initTypoCorrectionTracking();
    initFallbackClickTracking();
  }

  /* ===== PHASE 5: Search Unification v6 ===== */

  function initSearchUnification() {
    /* 1. CSS: suppress Vitals instant search + GF autocomplete */
    var s = document.createElement('style');
    s.textContent = '#vitals-instant-search,.vitals-search-modal,.vitals-search-overlay,[class*=vitals-search]{display:none!important;visibility:hidden!important;z-index:-1!important;pointer-events:none!important;width:0!important;height:0!important;overflow:hidden!important;position:fixed!important;top:-9999px!important;left:-9999px!important}.ui-autocomplete.ui-menu,.ui-autocomplete.ui-widget,.gf-search-overlay{display:none!important;visibility:hidden!important}.popular__search--history{display:none!important}#predictive__search_overlay.active #Search-In-Modal{width:100%!important;opacity:1!important;pointer-events:auto!important}';
    document.head.appendChild(s);

    // Native inputs must keep browser caret/selection behavior. Prevent retired
    // delegated search integrations from cancelling pointer defaults on these inputs.
    document.querySelectorAll('predictive-search input[name="q"], main-search input[name="q"]').forEach(function(input) {
      ['pointerdown', 'mousedown', 'touchstart', 'click', 'dblclick'].forEach(function(type) {
        input.addEventListener(type, function(event) { event.stopPropagation(); }, { capture: true });
      });
    });

    /* 2. Kill GF autocomplete */
    var killGF = function() {
      var ti = document.querySelector('.gf-autocomplete-input');
      if (ti && typeof jQuery !== 'undefined') { try { jQuery(ti).autocomplete('destroy'); } catch(x) {} }
    };
    killGF(); setTimeout(killGF, 2000); setTimeout(killGF, 5000);

    /* 3. Body observer: nuke Vitals container + hide GF menus */
    new MutationObserver(function() {
      document.querySelectorAll('.ui-autocomplete.ui-menu').forEach(function(m) { m.style.display = 'none'; });
      var vc = document.getElementById('vitals-instant-search');
      if (vc && vc.children.length > 0) { vc.innerHTML = ''; }
    }).observe(document.body, { childList: true, subtree: true });

    /* 4. Click-intercept: attach to all search-trigger buttons.
     * When user clicks search icon, the overlay opens. We use setTimeout
     * at staggered delays to catch the moment the input becomes visible
     * and call .focus(). */
    function attachSearchFocus() {
      var btns = document.querySelectorAll('[aria-label="Search our store"]');
      btns.forEach(function(b) {
        if (b._v6Attached) return;
        b._v6Attached = true;
        b.addEventListener('click', function() {
          var delays = [50, 100, 200, 400, 700];
          delays.forEach(function(d) {
            setTimeout(function() {
              var inp = document.getElementById('Search-In-Modal');
              var ov = document.getElementById('predictive__search_overlay');
              if (!inp || !ov) return;
              if (ov.classList.contains('active') && getComputedStyle(inp).visibility === 'visible' && document.activeElement !== inp) {
                inp.focus();
              }
            }, d);
          });
        }, true);
      });
    }
    attachSearchFocus();
    setTimeout(attachSearchFocus, 3000);

    // Focus is managed on open; never cover the input or controls with a label.

    /* 6. Overlay click fallback */
    document.addEventListener('click', function(e) {
      var ov = e.target.closest('#predictive__search_overlay');
      if (ov && !e.target.closest('#search__close_btn,a,button.search__button')) {
        var inp = document.getElementById('Search-In-Modal');
        if (inp && document.activeElement !== inp) {
          inp.focus();
        }
      }
    });
  }

  /* ===== INIT ===== */
  function init() {
    initSearchUnification();
    initAutoFocus();
    initKeyboardShortcut();
    initScrollLock();
    initVitalsSearchATC();
    initGFSearchATC();
    initSearchEnhancements();
    initSearchAnalytics();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
